import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getEducationItem } from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Clock } from 'lucide-react';

export default function EducationDetailPage() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getEducationItem(id);
        setArticle(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 text-center">
          <p className="text-muted-foreground">Article not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="education-detail-page">
      <Navbar />
      <div className="grain-overlay" />

      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        <Link to="/learn" className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm mb-8 transition-colors" data-testid="back-to-learn">
          <ArrowLeft className="w-4 h-4" /> Back to Learn
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="aspect-[2/1] overflow-hidden rounded-sm mb-8">
            <img src={article.image_url} alt={article.title} className="w-full h-full object-cover" />
          </div>

          <div className="flex items-center gap-3 mb-4">
            <Badge variant="outline" className="text-xs rounded-sm border-white/20 text-muted-foreground uppercase">
              {article.category}
            </Badge>
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="w-3 h-3" /> {article.read_time} min read
            </span>
          </div>

          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-6" data-testid="article-title">
            {article.title}
          </h1>

          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">{article.summary}</p>

          <div className="prose prose-invert max-w-none">
            {article.body.split('\n\n').map((para, i) => (
              <p key={i} className="text-foreground/80 leading-relaxed mb-4 text-base">
                {para}
              </p>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
